import { create } from "zustand";
import { generatePricetagsPdf as apiGeneratePricetagsPdf } from "../services/pricetagApiService";
import { generateCatalogPdf as apiGenerateCatalogPdf } from "../services/catalogApiService";
import { useErrorStore } from "components";
import { downloadFile } from "../utils/downloadHelper";

// Конфігурація для різних типів документів
const DOC_CONFIG = {
  pricetags: { maxLimit: 105, label: "цінники" },
  catalog: { maxLimit: 255, label: "каталог" },
};

// Глобальний стор для режиму вибору та генерації PDF
export const useDocumentGeneratorStore = create((set, get) => ({
  isSelectionModeActive: false,
  activeDocumentType: null, // 'pricetags' або 'catalog'
  selectedTemplate: null, // Обраний шаблон каталогу { id, name }
  selectedSkus: [],
  isLoading: false,

  // Переключення режиму з передачею типу документа та шаблону (для каталогу)
  toggleSelectionMode: (type = null, template = null) =>
    set((state) => ({
      isSelectionModeActive: !state.isSelectionModeActive,
      activeDocumentType: state.isSelectionModeActive ? null : type,
      selectedTemplate: state.isSelectionModeActive ? null : template,
      selectedSkus: [], // Очищаємо список при зміні/виході
    })),

  // Додавання SKU з урахуванням ліміту активного типу документа
  addSku: (sku) => {
    const { activeDocumentType, selectedSkus } = get();
    if (!activeDocumentType) return;

    const maxLimit = DOC_CONFIG[activeDocumentType].maxLimit;

    if (selectedSkus.length < maxLimit && !selectedSkus.includes(sku)) {
      set({ selectedSkus: [...selectedSkus, sku] });
    }
  },

  // Видалення SKU
  removeSku: (sku) =>
    set((state) => ({
      selectedSkus: state.selectedSkus.filter((s) => s !== sku),
    })),

  // "Обрати всі" (toggle): приймає масив усіх SKU на сторінці.
  // Якщо всі вже обрані — знімає їх, інакше додає з урахуванням ліміту.
  selectAll: (allSkus) =>
    set((state) => {
      const { activeDocumentType, selectedSkus } = state;
      if (!activeDocumentType || allSkus.length === 0) return {};

      const maxLimit = DOC_CONFIG[activeDocumentType].maxLimit;
      const allSelected = allSkus.every((sku) => selectedSkus.includes(sku));

      if (allSelected) {
        // Знімаємо виділення з усіх переданих
        return { selectedSkus: selectedSkus.filter((s) => !allSkus.includes(s)) };
      }

      // Додаємо всі (з дедупом) і обрізаємо до ліміту
      const merged = Array.from(new Set([...selectedSkus, ...allSkus]));
      return { selectedSkus: merged.slice(0, maxLimit) };
    }),

  // Універсальна генерація
  generateAndDownloadPdf: async (warehouse_id = null) => {
    const { selectedSkus, activeDocumentType, selectedTemplate } = get();
    if (selectedSkus.length === 0 || !activeDocumentType) return;

    set({ isLoading: true });
    try {
      let blob;

      // Визначаємо, який API викликати
      if (activeDocumentType === "catalog") {
        blob = await apiGenerateCatalogPdf(selectedSkus, selectedTemplate?.id);
      } else if (activeDocumentType === "pricetags") {
        blob = await apiGeneratePricetagsPdf(selectedSkus, warehouse_id);
      }

      // Формуємо назву PDF-файлу
      let filename;
      if (activeDocumentType === "catalog") {
        const shopName = selectedTemplate?.name;
        filename = shopName
          ? `Каталог товарів ${shopName}.pdf`
          : "Каталог товарів.pdf";
      } else {
        const now = new Date();
        const date = now.toLocaleDateString("uk-UA");
        const time = now
          .toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
          .replace(":", "-");
        const quantity = selectedSkus.length;
        filename = `${date} ${time} цінники ${quantity} шт.pdf`;
      }

      // Завантажуємо PDF
      downloadFile(blob, filename);

      // Скидаємо стан
      get().resetState();
    } catch (error) {
      const { showError } = useErrorStore.getState();
      const docLabel = DOC_CONFIG[activeDocumentType].label;
      showError(`Не вдалося згенерувати ${docLabel}: ${error.message}`);
      console.error(`Помилка генерації ${docLabel}:`, error);

      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Скидання стану
  resetState: () =>
    set({
      isSelectionModeActive: false,
      activeDocumentType: null,
      selectedTemplate: null,
      selectedSkus: [],
      isLoading: false,
    }),
}));
